import { isPathbuddyHost, isEduPortal } from './eduHost';
import { stripAppBasename } from './appBase';
import { isIvoriApexHost, isCSuiteHost } from './ivoriHost';
import {
  applyEduDocumentSeo,
  eduMarketingDescription,
  eduMarketingTitle,
  usesEduMarketingMeta,
} from './eduSeo';

/**
 * Centralized, unique Page Title resolver.
 * Ensures every single route, subdomain, and specialty path has a distinct,
 * high-converting, and SEO-optimized title tag.
 */
export function resolvePageTitle(pathname, hostname) {
  const h = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase();
  // pathname is router-relative (no /edu); the window fallback has to drop the prefix too.
  const path = (pathname || (typeof window !== 'undefined' ? stripAppBasename(window.location.pathname) : '/') || '/').toLowerCase();
  const onPathbuddy = isPathbuddyHost(h);

  // 1. Subdomain Root Checks (when on '/')
  if (path === '/') {
    if (h.startsWith('lnat.') || h.includes('lnat.ivori') || h.includes('lnat.edu')) {
      return 'LNAT Exam Prep 2026 Mock Papers & Essay Feedback | ivori';
    }
    if (h.startsWith('tmua.') || h.includes('tmua.ivori') || h.includes('tmua.edu')) {
      return 'TMUA Prep 2026 — Paper 1 & 2 Math Reasoning | ivori';
    }
    if (h.startsWith('ucat.') || h.includes('ucat.ivori') || h.includes('ucat.edu')) {
      return 'UCAT Medical Exam Prep 2026 Diagnostic Mocks | ivori';
    }
    if (h.startsWith('esat.') || h.includes('esat.ivori') || h.includes('esat.edu')) {
      return 'ESAT Engineering & Science Admissions Test Prep 2026 | ivori';
    }
    if (h.startsWith('tara.') || h.includes('tara.ivori') || h.includes('tara.edu')) {
      return 'TARA — AI Analytical Reasoning & Assessment Engine | ivori';
    }
    if (h.startsWith('step.') || h.includes('step.ivori')) {
      return 'STEP Mathematics 2 & 3 Advanced Exam Preparation | ivori';
    }
    if (h.startsWith('oxbridge.') || h.includes('oxbridge.edu') || h.includes('oxbridge.ivori')) {
      return 'Oxbridge Interview Simulations & Supervision Prep | ivori';
    }
    if (h.startsWith('medicalschool.') || h.includes('medicalschool.edu') || h.includes('medicalschool.ivori')) {
      return 'Medical School MMI Stations & Panel Interview Prep | ivori';
    }
    if (h.startsWith('degreeapprenticeship.') || h.includes('degreeapprenticeship.edu') || h.includes('degreeapprenticeship.ivori')) {
      return 'Degree Apprenticeship Assessment Center & Interview Prep | ivori';
    }
    if (h.startsWith('tsa.') || h.includes('tsa.ivori')) {
      return 'TSA Oxford Thinking Skills Assessment Mock Exams | ivori';
    }
    if (h.startsWith('lsat.') || h.includes('lsat.ivori')) {
      return 'LSAT Law School Admission Test Logic & Reasoning | ivori';
    }
    if (h.startsWith('astonmmi.') || h.startsWith('ammi.')) {
      return 'Aston Medical School MMI Stations & Mock Circuit | ivori';
    }
    if (h.startsWith('birminghammmi.') || h.startsWith('bmmi.')) {
      return 'Birmingham Medical School MMI Interview Simulator | ivori';
    }
    if (h.startsWith('cmp.') || h.startsWith('cambridgemedicalpanel.')) {
      return 'Cambridge Medical Panel Interview Simulator | ivori';
    }
    // Edu portal: the edu hosts, or ivoriai.com under /edu (company home is not).
    if (isEduPortal(h)) {
      if (onPathbuddy) return 'Pathbuddy — Master Any Exam. Master Any Interview.';
      const eduTitle = eduMarketingTitle('/');
      applyEduDocumentSeo('/', h, eduTitle, eduMarketingDescription('/'));
      return eduTitle;
    }
    if (isCSuiteHost(h)) {
      return 'ivori C-Suite — Executive Simulation & Decision Intelligence';
    }
    if (isIvoriApexHost(h)) {
      return 'IVORI — AI for Decision-Makers | IVORI Edu';
    }
    if (onPathbuddy) {
      return 'Pathbuddy — Master Any Exam. Master Any Interview.';
    }
  }

  // Company home, moved from ivoriai.com/ (which now redirects to /edu).
  if (path === '/about' && isIvoriApexHost(h) && !isEduPortal(h)) {
    return 'IVORI — AI for Decision-Makers | IVORI Edu';
  }

  if (usesEduMarketingMeta(h)) {
    const eduTitle = eduMarketingTitle(path);
    if (eduTitle) {
      applyEduDocumentSeo(path, h, eduTitle, eduMarketingDescription(path));
      return eduTitle;
    }
  }

  // 2. Exact Route Title Mappings
  const ROUTE_TITLES = {
    '/login': onPathbuddy ? 'Sign In — Pathbuddy' : 'Sign In — ivori',
    '/profile': onPathbuddy ? 'Account & Profile Settings — Pathbuddy' : 'Account & Profile Settings | ivori',
    '/pricing': 'Pricing & Plans — ivori Edu',
    '/upgrade': 'Pricing & Plans — ivori Edu',
    '/plans': 'Pricing & Plans — ivori Edu',
    '/subscription/success': 'Purchase Confirmed — Welcome to ivori Edu',
    '/apps': 'App Store & Case Simulators | ivori Edu',
    '/apps-store': 'App Store & Case Simulators | ivori Edu',
    '/cases': 'Decision & Case Studies Sandbox | ivori',
    '/case-studies': 'Strategic Case Studies Sandbox | ivori',
    '/notes': 'IVORI Notes — Academic Synthesis & Socratic Defence',
    '/chat': 'IVORI Chat — Decision & Admissions Intelligence',
    '/lnat': 'LNAT Exam Prep 2026 Mock Papers & Essay Feedback | ivori',
    '/lnat-prep': 'LNAT Exam Prep 2026 Mock Papers & Essay Feedback | ivori',
    '/specialty/lnat': 'LNAT Exam Prep 2026 Mock Papers & Essay Feedback | ivori',
    '/tmua': 'TMUA Prep 2026 — Paper 1 & 2 Math Reasoning | ivori',
    '/tmua-prep': 'TMUA Prep 2026 — Paper 1 & 2 Math Reasoning | ivori',
    '/specialty/tmua': 'TMUA Prep 2026 — Paper 1 & 2 Math Reasoning | ivori',
    '/ucat': 'UCAT Medical Exam Prep 2026 Diagnostic Mocks | ivori',
    '/ucat-prep': 'UCAT Medical Exam Prep 2026 Diagnostic Mocks | ivori',
    '/specialty/ucat': 'UCAT Medical Exam Prep 2026 Diagnostic Mocks | ivori',
    '/esat': 'ESAT Engineering & Science Admissions Test Prep 2026 | ivori',
    '/esat-prep': 'ESAT Engineering & Science Admissions Test Prep 2026 | ivori',
    '/specialty/esat': 'ESAT Engineering & Science Admissions Test Prep 2026 | ivori',
    '/tara': 'TARA — AI Analytical Reasoning & Assessment Engine | ivori',
    '/tara-prep': 'TARA — AI Analytical Reasoning & Assessment Engine | ivori',
    '/specialty/tara': 'TARA — AI Analytical Reasoning & Assessment Engine | ivori',
    '/oxbridge': 'Oxbridge Interview Simulations & Supervision Prep | ivori',
    '/oxbridge-prep': 'Oxbridge Interview Simulations & Supervision Prep | ivori',
    '/specialty/oxbridge': 'Oxbridge Interview Simulations & Supervision Prep | ivori',
    '/medicalschool': 'Medical School MMI Stations & Panel Interview Prep | ivori',
    '/medicalschool-prep': 'Medical School MMI Stations & Panel Interview Prep | ivori',
    '/specialty/medicalschool': 'Medical School MMI Stations & Panel Interview Prep | ivori',
    '/degreeapprenticeship': 'Degree Apprenticeship Assessment Center & Interview Prep | ivori',
    '/degreeapprenticeship-prep': 'Degree Apprenticeship Assessment Center & Interview Prep | ivori',
    '/specialty/degreeapprenticeship': 'Degree Apprenticeship Assessment Center & Interview Prep | ivori',
    '/degree-apprenticeships': 'Degree Apprenticeship Assessment Center & Interview Prep | ivori',
    '/degree-apprenticeships-prep': 'Degree Apprenticeship Assessment Center & Interview Prep | ivori',
    '/specialty/degree-apprenticeships': 'Degree Apprenticeship Assessment Center & Interview Prep | ivori',
    '/practice': 'Interactive Admissions Practice Hub | ivori Edu',
    '/practice/lnat': 'LNAT Timed Practice & Section A Drills | ivori',
    '/practice/tmua': 'TMUA Paper 1 & 2 Practice Breakdown | ivori',
    '/practice/ucat': 'UCAT Timed Diagnostic Mock Station | ivori',
    '/practice/esat': 'ESAT Modular Practice Questions | ivori',
    '/practice/step': 'STEP Mathematics Practice Workspace | ivori',
    '/practice/lsat': 'LSAT Logic Games & Reasoning Workspace | ivori',
    '/quiz': 'Interactive Diagnostic Assessment Quiz | ivori Edu',
    '/interview': 'AI Interview Simulation Suite | ivori Edu',
    '/teacher-dashboard': 'Instructor & Teacher Dashboard | ivori Edu',
    '/teacher-tools': 'Teacher Assessment & Curriculum Tools | ivori Edu',
    '/studio': 'Case & Exam Creation Studio | ivori',
    '/studio-cases': 'Case Authoring Studio | ivori',
    '/studio-exams': 'Exam Authoring Studio | ivori',
    '/flowforge': 'FlowForge — Interactive Workflow Designer | ivori',
    '/custom-quiz-generator': 'Custom Quiz & Exam Generator | ivori Edu',
    '/flash-cards': 'Active Recall & Smart Flashcards | ivori Edu',
    '/caius-explore': 'Caius Explore & Supercurricular Research | ivori',
    '/code-lab': 'Code Lab — Computational Modeling & Python | ivori',
    '/library': 'Smart Supercurricular Academic Library | ivori',
    '/supercurricular-books': 'Essential Supercurricular Reading Lists | ivori',
    '/creators': 'Creators & Research Fellows Directory | ivori',
    '/verify': 'Certificate Verification Portal | ivori',
    '/event-vault': 'Event SharePoint & Media Vault | ivori',
    '/events': 'Admissions & Masterclass Event Schedule | ivori',
    '/reset-password': 'Reset Your Password | ivori',
    '/terms': 'Terms of Service | ivori',
    '/privacy': 'Privacy Policy & Data Safeguards | ivori',
    '/legal': 'Legal & Compliance Hub | ivori',
    '/support': 'Support & Help Centre | ivori'
  };

  if (ROUTE_TITLES[path]) {
    return ROUTE_TITLES[path];
  }

  // 3. Prefix matching for dynamic routes
  if (path.startsWith('/practice/')) {
    const exam = path.replace('/practice/', '').toUpperCase();
    return `${exam} Practice Workspace & Timed Drills | ivori`;
  }
  if (path.startsWith('/case-study/')) {
    return 'Case Study Simulation | ivori';
  }
  if (path.startsWith('/interviews/')) {
    return 'Interview Simulation Workspace | ivori Edu';
  }
  if (path.startsWith('/apps-store/creator/')) {
    return 'Creator Profile & Case Apps | ivori';
  }
  if (path.startsWith('/apps-store/')) {
    return 'App Detail & Interactive Simulator | ivori Edu';
  }
  if (path.startsWith('/store/')) {
    return 'Store App Simulation | ivori Edu';
  }
  if (path.startsWith('/verify/')) {
    return 'Verified Certificate Record | ivori';
  }

  return onPathbuddy
    ? 'Pathbuddy — Master Any Exam. Master Any Interview.'
    : 'ivori — AI That Sharpens Judgment, Reasoning, Decisions';
}

/**
 * Centralized, unique Meta Description resolver.
 * Ensures every single route, subdomain, and specialty path has a tailored,
 * 140-160 character description to eliminate duplicate meta description warnings.
 */
export function resolvePageDescription(pathname, hostname) {
  const h = (hostname || (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase();
  const path = (pathname || (typeof window !== 'undefined' ? stripAppBasename(window.location.pathname) : '/') || '/').toLowerCase();
  const onPathbuddy = isPathbuddyHost(h);

  // 1. Subdomain Root Checks (when on '/')
  if (path === '/') {
    if (h.startsWith('lnat.') || h.includes('lnat.ivori') || h.includes('lnat.edu')) {
      return 'Master the LNAT with authentic Pearson VUE Section A computer mock exams and sub-15s AI Section B essay evaluation calibrated to Oxford and UCL law rubrics.';
    }
    if (h.startsWith('tmua.') || h.includes('tmua.ivori') || h.includes('tmua.edu')) {
      return 'Ace the TMUA 2026 for Cambridge, Imperial and LSE. Practice Paper 1 applications and Paper 2 formal mathematical proof logic breakdowns.';
    }
    if (h.startsWith('ucat.') || h.includes('ucat.ivori') || h.includes('ucat.edu')) {
      return 'Prepare for UCAT 2026 with timed computer-based diagnostic mock tests across Verbal, Decision Making, Quantitative, Abstract and Situational Judgement.';
    }
    if (h.startsWith('esat.') || h.includes('esat.ivori') || h.includes('esat.edu')) {
      return 'Prepare for the Engineering and Science Admissions Test (ESAT) for Cambridge and Imperial across Mathematics, Physics, Chemistry, and Biology modules.';
    }
    if (h.startsWith('tara.') || h.includes('tara.ivori') || h.includes('tara.edu')) {
      return 'Benchmark academic aptitude and analytical reasoning with TARA proprietary multi-trait AI assessment and diagnostic scoring engine.';
    }
    if (h.startsWith('step.') || h.includes('step.ivori')) {
      return 'Master Cambridge STEP 2 and STEP 3 Mathematics with in-depth problem-solving sets, calculus proofs, and worked mechanics rationale.';
    }
    if (h.startsWith('oxbridge.') || h.includes('oxbridge.edu') || h.includes('oxbridge.ivori')) {
      return 'AI-simulated Oxbridge interview preparations, Socratic supervisions, and admissions roadmaps tailored for Oxford and Cambridge applicants.';
    }
    if (h.startsWith('medicalschool.') || h.includes('medicalschool.edu') || h.includes('medicalschool.ivori')) {
      return 'Master medical school admissions with AI-driven MMI circuit stations, ethical scenario roleplays, and traditional clinical panel interview simulations.';
    }
    if (h.startsWith('degreeapprenticeship.') || h.includes('degreeapprenticeship.edu') || h.includes('degreeapprenticeship.ivori')) {
      return 'Prepare for top UK degree apprenticeships in banking, tech, and consulting with authentic assessment center tests and commercial case study simulations.';
    }
    if (h.startsWith('tsa.') || h.includes('tsa.ivori')) {
      return 'Prepare for the Oxford Thinking Skills Assessment (TSA) with comprehensive critical thinking, problem-solving, and essay practice suites.';
    }
    if (h.startsWith('lsat.') || h.includes('lsat.ivori')) {
      return 'Practice LSAT analytical reasoning, logic games, reading comprehension, and deductive logical arguments with instant scoring.';
    }
    if (h.startsWith('astonmmi.') || h.startsWith('ammi.')) {
      return 'Simulate Aston Medical School MMI stations with AI patient roleplays, communication evaluation, and structured clinical feedback.';
    }
    if (h.startsWith('birminghammmi.') || h.startsWith('bmmi.')) {
      return 'Practice University of Birmingham Medical School MMI stations with ethical scenarios, calculations, and clinical roleplay simulations.';
    }
    if (h.startsWith('cmp.') || h.startsWith('cambridgemedicalpanel.')) {
      return 'Cambridge Medical Panel simulation for pre-clinical medicine applicants. Practice scientific cross-examinations and ethical discussions.';
    }
    if (isEduPortal(h)) {
      if (onPathbuddy) {
        return 'Master any exam. Master any interview. Tell Pathbuddy what you are prepping for and unlock authentic mock simulations and AI feedback.';
      }
      const eduDescription = eduMarketingDescription('/');
      applyEduDocumentSeo('/', h, eduMarketingTitle('/'), eduDescription);
      return eduDescription;
    }
    if (isCSuiteHost(h)) {
      return 'Train strategic leadership and executive decision making through realistic high-stakes scenario simulations with ivori C-Suite.';
    }
    if (isIvoriApexHost(h)) {
      return 'IVORI is AI for decision-makers, from the classroom to the boardroom. IVORI Edu for students and schools, built on research from IVORI Lab.';
    }
    if (onPathbuddy) {
      return 'Master any exam. Master any interview. Tell Pathbuddy what you are prepping for and unlock authentic mock simulations and AI feedback.';
    }
  }

  if (path === '/about' && isIvoriApexHost(h) && !isEduPortal(h)) {
    return 'IVORI is AI for decision-makers, from the classroom to the boardroom. IVORI Edu for students and schools, built on research from IVORI Lab.';
  }

  if (usesEduMarketingMeta(h)) {
    const eduDescription = eduMarketingDescription(path);
    if (eduDescription != null) {
      applyEduDocumentSeo(path, h, eduMarketingTitle(path), eduDescription);
      return eduDescription;
    }
  }

  // 2. Exact Route Descriptions
  const ROUTE_DESCS = {
    '/login': onPathbuddy
      ? 'Sign in to your Pathbuddy account to access your admissions roadmap, mock exam test banks, and interactive interview simulators.'
      : 'Sign in to your ivori account to access admissions mock exam simulators, personal statement apps, and research workspaces.',
    '/profile': onPathbuddy
      ? 'Manage your Pathbuddy account profile, subscription plans, primary subject track, and candidate timeline settings.'
      : 'Manage your ivori account, subscription tier entitlements, subject preferences, and candidate track settings.',
    '/pricing': 'Explore ivori Edu plans — Beginnings (free), Foundations (£60) and Beyond (£80). One-off payment for each exam, with an optional interview package.',
    '/upgrade': 'Explore ivori Edu plans — a one-off payment for each exam, with an optional interview package.',
    '/plans': 'Compare ivori Edu plans for LNAT, TMUA, UCAT, ESAT and TARA. One-off payment for each exam.',
    '/subscription/success': 'Thank you for your purchase. Your plan is unlocked and ready for practice.',
    '/apps': 'Explore the ivori App Store for interactive case study sandboxes, subject productivity tools, and computational modelers.',
    '/apps-store': 'Explore the ivori App Store for interactive case study sandboxes, subject productivity tools, and computational modelers.',
    '/cases': 'Test strategic decision-making with real-world valuation, policy, and business case study simulations in ivori.',
    '/case-studies': 'Test strategic decision-making with real-world valuation, policy, and business case study simulations in ivori.',
    '/notes': 'IVORI Notes: AI academic synthesis workspace with Socratic Oral Defence Simulator, Toulmin argument auditor, and audio overviews.',
    '/chat': 'Chat with IVORI Decision and Admissions AI engine for deep reasoning, problem-solving, and personalized academic feedback.',
    '/lnat': 'Master the LNAT with authentic Pearson VUE Section A computer mock exams and sub-15s AI Section B essay evaluation calibrated to Oxford and UCL law rubrics.',
    '/lnat-prep': 'Master the LNAT with authentic Pearson VUE Section A computer mock exams and sub-15s AI Section B essay evaluation calibrated to Oxford and UCL law rubrics.',
    '/specialty/lnat': 'Master the LNAT with authentic Pearson VUE Section A computer mock exams and sub-15s AI Section B essay evaluation calibrated to Oxford and UCL law rubrics.',
    '/tmua': 'Ace the TMUA 2026 for Cambridge, Imperial and LSE. Practice Paper 1 applications and Paper 2 formal mathematical proof logic breakdowns.',
    '/tmua-prep': 'Ace the TMUA 2026 for Cambridge, Imperial and LSE. Practice Paper 1 applications and Paper 2 formal mathematical proof logic breakdowns.',
    '/specialty/tmua': 'Ace the TMUA 2026 for Cambridge, Imperial and LSE. Practice Paper 1 applications and Paper 2 formal mathematical proof logic breakdowns.',
    '/ucat': 'Prepare for UCAT 2026 with timed computer-based diagnostic mock tests across Verbal, Decision Making, Quantitative, Abstract and Situational Judgement.',
    '/ucat-prep': 'Prepare for UCAT 2026 with timed computer-based diagnostic mock tests across Verbal, Decision Making, Quantitative, Abstract and Situational Judgement.',
    '/specialty/ucat': 'Prepare for UCAT 2026 with timed computer-based diagnostic mock tests across Verbal, Decision Making, Quantitative, Abstract and Situational Judgement.',
    '/esat': 'Prepare for the Engineering and Science Admissions Test (ESAT) for Cambridge and Imperial across Mathematics, Physics, Chemistry, and Biology modules.',
    '/esat-prep': 'Prepare for the Engineering and Science Admissions Test (ESAT) for Cambridge and Imperial across Mathematics, Physics, Chemistry, and Biology modules.',
    '/specialty/esat': 'Prepare for the Engineering and Science Admissions Test (ESAT) for Cambridge and Imperial across Mathematics, Physics, Chemistry, and Biology modules.',
    '/tara': 'Benchmark academic aptitude and analytical reasoning with TARA proprietary multi-trait AI assessment and diagnostic scoring engine.',
    '/tara-prep': 'Benchmark academic aptitude and analytical reasoning with TARA proprietary multi-trait AI assessment and diagnostic scoring engine.',
    '/specialty/tara': 'Benchmark academic aptitude and analytical reasoning with TARA proprietary multi-trait AI assessment and diagnostic scoring engine.',
    '/oxbridge': 'AI-simulated Oxbridge interview preparations, Socratic supervisions, and admissions roadmaps tailored for Oxford and Cambridge applicants.',
    '/oxbridge-prep': 'AI-simulated Oxbridge interview preparations, Socratic supervisions, and admissions roadmaps tailored for Oxford and Cambridge applicants.',
    '/specialty/oxbridge': 'AI-simulated Oxbridge interview preparations, Socratic supervisions, and admissions roadmaps tailored for Oxford and Cambridge applicants.',
    '/medicalschool': 'Master medical school admissions with AI-driven MMI circuit stations, ethical scenario roleplays, and traditional clinical panel interview simulations.',
    '/medicalschool-prep': 'Master medical school admissions with AI-driven MMI circuit stations, ethical scenario roleplays, and traditional clinical panel interview simulations.',
    '/specialty/medicalschool': 'Master medical school admissions with AI-driven MMI circuit stations, ethical scenario roleplays, and traditional clinical panel interview simulations.',
    '/degreeapprenticeship': 'Prepare for top UK degree apprenticeships in banking, tech, and consulting with authentic assessment center tests and commercial case study simulations.',
    '/degreeapprenticeship-prep': 'Prepare for top UK degree apprenticeships in banking, tech, and consulting with authentic assessment center tests and commercial case study simulations.',
    '/specialty/degreeapprenticeship': 'Prepare for top UK degree apprenticeships in banking, tech, and consulting with authentic assessment center tests and commercial case study simulations.',
    '/degree-apprenticeships': 'Prepare for top UK degree apprenticeships in banking, tech, and consulting with authentic assessment center tests and commercial case study simulations.',
    '/degree-apprenticeships-prep': 'Prepare for top UK degree apprenticeships in banking, tech, and consulting with authentic assessment center tests and commercial case study simulations.',
    '/specialty/degree-apprenticeships': 'Prepare for top UK degree apprenticeships in banking, tech, and consulting with authentic assessment center tests and commercial case study simulations.',
    '/practice': 'Access timed past papers, mock test simulations, and diagnostic question banks across all UK university admissions tests.',
    '/practice/lnat': 'Practice LNAT Section A multiple-choice questions under timed conditions with instant answer explanations and skill breakdown.',
    '/practice/tmua': 'Work through TMUA Paper 1 and Paper 2 mathematical reasoning questions with step-by-step logic proofs and rationale.',
    '/practice/ucat': 'Practice timed UCAT subtests with official calculator shortcuts, keyboard controls, and diagnostic performance tracking.',
    '/practice/esat': 'Solve modular ESAT practice questions in Mathematics, Physics, Chemistry, and Biology calibrated to Imperial and Cambridge standards.',
    '/practice/step': 'Solve advanced Cambridge STEP 2 and STEP 3 Mathematics problems with complete worked solutions and step rationale.',
    '/practice/lsat': 'Solve official-style LSAT logic games and analytical deduction problems with automated validity checkers.',
    '/quiz': 'Take a calibrated diagnostic assessment to benchmark your critical thinking, mathematical reasoning, and verbal aptitude.',
    '/interview': 'Practice AI-simulated university admissions interviews with real-time follow-up questioning and rubric evaluation.',
    '/teacher-dashboard': 'Instructor dashboard for educators to monitor student mock test performances, essay scores, and cohort analytics.',
    '/teacher-tools': 'Access curriculum authoring, cohort test assignment, and grading tools designed for tutors and school educators.',
    '/studio': 'Author interactive case studies, assessment quizzes, and exam papers using the ivori Studio creation suite.',
    '/studio-cases': 'Design and publish interactive business, legal, and engineering decision case studies in ivori Studio.',
    '/studio-exams': 'Author admissions mock exam papers, mark schemes, and worked answer breakdowns in ivori Studio.',
    '/flowforge': 'Build and simulate complex logical and strategic workflows visually with ivori FlowForge.',
    '/custom-quiz-generator': 'Generate custom admissions and subject quizzes tailored by topic, difficulty, and target university requirements.',
    '/flash-cards': 'Master active recall and spaced repetition for admissions test formulas, legal principles, and medical ethics.',
    '/caius-explore': 'Explore supercurricular essay questions and research prompts inspired by Gonville & Caius College Cambridge.',
    '/code-lab': 'Explore computational modeling, quantitative valuation, and Python algorithms in the ivori Code Lab.',
    '/library': 'Browse smart supercurricular reading recommendations and primary research papers for university applicants.',
    '/supercurricular-books': 'Essential reading lists for competitive university applications across Law, Medicine, CS, and Economics.',
    '/creators': 'Discover academic fellows, researchers, and educators authoring case apps and learning tools on ivori.',
    '/verify': 'Verify the authenticity and completion status of an official ivori Certificate of Achievement.',
    '/event-vault': 'Watch recordings and review slides from past ivori admissions masterclasses and workshops.',
    '/events': 'View upcoming live masterclasses, mock interview clinics, and admissions strategy webinars from ivori.',
    '/reset-password': 'Reset your ivori account password securely via email verification link.',
    '/terms': 'Read the Terms of Service governing access to the ivori AI platform, educational portals, and subscription services.',
    '/privacy': 'Learn how ivori protects user data in compliance with UK GDPR, the Data Protection Act 2018, and the ICO Children Code.',
    '/legal': 'Access legal disclosures, data compliance documentation, and corporate policies for ivori and PATHBUDDY LIMITED.',
    '/support': 'Get assistance from the ivori support team with account access, billing, subscription tiers, or technical issues.'
  };

  if (ROUTE_DESCS[path]) {
    return ROUTE_DESCS[path];
  }

  // 3. Dynamic route descriptions
  if (path.startsWith('/practice/')) {
    const exam = path.replace('/practice/', '').toUpperCase();
    return `Practice timed ${exam} mock tests and diagnostic question banks with instant scoring and detailed answer explanations on ivori.`;
  }
  if (path.startsWith('/case-study/')) {
    return 'Engage in an interactive strategic decision-making simulation with empirical data analysis and structured debriefs on ivori.';
  }
  if (path.startsWith('/interviews/')) {
    return 'Step into an AI-simulated oral supervision and interview workspace with personalized Socratic questioning and structured feedback.';
  }
  if (path.startsWith('/apps-store/creator/')) {
    return 'Explore specialized learning apps, simulation sandboxes, and tools created by this verified ivori contributor.';
  }
  if (path.startsWith('/apps-store/')) {
    return 'Launch this interactive case simulator or subject tool to train decision-making under realistic constraints on ivori Edu.';
  }
  if (path.startsWith('/store/')) {
    return 'Launch this interactive case simulator or subject tool to train decision-making under realistic constraints on ivori Edu.';
  }
  if (path.startsWith('/verify/')) {
    return 'Verify the authenticity and credentials of an issued ivori Certificate of Achievement.';
  }

  return onPathbuddy
    ? 'Master any exam. Master any interview. Tell Pathbuddy what you are prepping for and unlock authentic mock simulations and AI feedback.'
    : 'ivori provides the Decision World Model — causal AI and counterfactual reasoning built for high-stakes decisions with compounding consequences.';
}

export default resolvePageTitle;
